import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../../redux/store';
import { createClientApi, getClientByIdApi, updateClientApi } from '../../../../../redux/slices/ClientSlice';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

const ClientForm = () => {
    const { id } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const navigate = useNavigate();

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        phone: Yup.string()
            .matches(/^\d{10,}$/, 'Phone must be at least 10 digits')
            .required('Phone is required'),
        role_id: Yup.number()
            .oneOf([1, 2], 'Role must be either Dealer or Carpenter')
            .required('Role is required'),
        // profile_image: Yup.mixed().required('Profile image is required'),
    });
    
    // Formik configuration
    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            phone: '',
            address: '',
            profile_image: null as File | null, // Ensure the type is File or null
            is_active: 1,
            role_id: 2, // Default to Dealer (1 = Dealer, 2 = Carpenter)
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('email', values.email);
            formData.append('phone', values.phone);
            formData.append('address', values.address);
            formData.append('is_active', values.is_active.toString());
            formData.append('role_id', values.role_id.toString());
            if (values.profile_image) {
                formData.append('profile_image', values.profile_image);
            }

            let data;
            if (id) {
                // Update client API call
                data = await dispatch(updateClientApi({ id, payload: formData }));
            } else {
                // Create new client API call
                data = await dispatch(createClientApi(formData));
            }

            if (data.payload.success) {
                setLoading(false);
                toast.success(data.payload.reason);
            } else {
                setLoading(false);
                toast.error(data.payload.reason ? data.payload.reason : 'Something went wrong');
            }
            navigate(-1);
        },
    });

    useEffect(() => {
        if (id) {
            fetchClientData();
        }
    }, [id]);

    // const fetchClientData = async () => {
    //   const response = await dispatch(getClientByIdApi(id))
    //   if (response.payload.success) {
    //     formik.setValues(response.payload.data?.data)
    //   } else {
    //     toast.error('Failed to fetch client data')
    //   }
    // }
    const fetchClientData = async () => {
        dispatch(getClientByIdApi(id)).then((response) => {
            if (response.payload) {
                const fetchedData = response.payload.data?.data || response.payload;
                console.log(fetchedData)
                formik.setValues({
                    ...fetchedData,
                    profile_image: fetchedData.profile_image_url, // Keep this null to handle file inputs correctly
                    role_id: fetchedData.role_id || 1, // Default to Dealer if role_id is missing
                });

                if (fetchedData.profile_image_url) {
                    setImagePreview(fetchedData.profile_image_url);
                } else {
                    setImagePreview(null);
                }
            } else {
                toast.error('Failed to fetch client data');
            }
        }).catch(() => {
            toast.error('An error occurred while fetching data');
        });
    };

    const handleDownload = () => {
        if (imagePreview) {
            fetch(imagePreview)
                .then((response) => response.blob()) // Fetch the image as a blob
                .then((blob) => {
                    const url = window.URL.createObjectURL(blob); // Create a blob URL
                    const link = document.createElement('a'); // Create a download link
                    link.href = url; // Set the blob URL as the href
                    link.download = 'profile_image_url.png'; // Set the file name to profile_image_url.png
                    document.body.appendChild(link); // Append link for browser compatibility
                    link.click(); // Trigger the download
                    document.body.removeChild(link); // Remove the link after download
                    window.URL.revokeObjectURL(url); // Release memory
                })
                .catch((error) => {
                    console.error('Failed to download image:', error); // Handle fetch errors
                });
        } else {
            console.error('Image preview is not available'); // Handle missing image
        }
    };

    return (
        <div className="container mt-4">
            <div className="card shadow" style={{ height: 'auto', minHeight: '500px' }}>
                <form onSubmit={formik.handleSubmit}>
                    {/* Card Header */}
                    <div className="card-header bg-light d-flex justify-content-between align-items-center position-relative py-6">
                        <h2 className="mb-0">{id ? 'Edit Client' : 'Add Client'}</h2>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Saving...' : id ? 'Update' : 'Submit'}
                        </button>
                        <span
                            className={`position-absolute border-primary`}
                            style={{
                                top: '12px',
                                left: '0',
                                height: '70%',
                                width: '4px',
                                backgroundColor: 'var(--bs-primary)',
                            }}
                        ></span>
                    </div>

                    {/* Card Body */}
                    <div className="card-body p-6">
                        <div className="row g-3">
                             {/* Name */}
                             <div className="col-md-6">
                                <label htmlFor="name" className="form-label">
                                    Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    className="form-control"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter client name"
                                />
                                {formik.touched.name && formik.errors.name && (
                                    <div className="text-danger">{formik.errors.name}</div>
                                )}
                            </div>

                              {/* Phone */}
                            <div className="col-md-6">
                                <label htmlFor="phone" className="form-label">
                                    Phone <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    className="form-control"
                                    value={formik.values.phone}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter phone number"
                                />
                                {formik.touched.phone && formik.errors.phone && (
                                    <div className="text-danger">{formik.errors.phone}</div>
                                )}
                            </div>

                            {/* Email */}
                            <div className="col-md-6">
                                <label htmlFor="email" className="form-label">
                                    Email 
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className="form-control"
                                    value={formik.values.email}
                                    onChange={(e) => {
                                        formik.setFieldValue('email', e.target.value);
                                    }}
                                    placeholder="Enter email address"
                                />
                            </div>

                            {/* Status */}
                            <div className="col-md-6">
                                <label htmlFor="status" className="form-label me-3">
                                    Status
                                </label>
                                <select
                                    id="status"
                                    name="status"
                                    className="form-select"
                                    value={formik.values.is_active}
                                    onChange={(e) => {
                                        const value = e.target.value === '1' ? 1 : 0;
                                        formik.setFieldValue('is_active', value);
                                    }}
                                >
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </select>
                            </div>

                            {/* Role */}
                            <div className="col-md-6">
                                <label htmlFor="role_id" className="form-label">
                                    Role <span className="text-danger">*</span>
                                </label>
                                <select
                                    id="role_id"
                                    name="role_id"
                                    className="form-select"
                                    value={formik.values.role_id}
                                    onChange={(e) => {
                                        formik.setFieldValue('role_id', parseInt(e.target.value));
                                    }}
                                    onBlur={formik.handleBlur}
                                >
                                    <option value={1}>Dealer</option>
                                    <option value={2}>Carpenter</option>
                                </select>
                                {formik.touched.role_id && formik.errors.role_id && (
                                    <div className="text-danger">{formik.errors.role_id}</div>
                                )}
                            </div>


                            {/* Client Image */}
                            <div className="col-md-6">
                                <label htmlFor="profileImage" className="form-label">
                                    Profile Image 
                                    {/* <span className="text-danger">*</span> */}
                                </label>
                                <input
                                    type="file"
                                    id="profile_image"
                                    name="profile_image"
                                    className="form-control"
                                    onChange={(e) => {
                                        if (e.target.files?.[0]) {
                                            formik.setFieldValue('profile_image', e.target.files[0]);
                                            setImagePreview(URL.createObjectURL(e.target.files[0]));
                                        }
                                    }}
                                />
                                {/* {formik.touched.profile_image && formik.errors.profile_image && (
                                    <div className="text-danger">{formik.errors.profile_image}</div>
                                )} */}
                                {imagePreview && (
                                    <div className="mt-3">
                                        <img src={imagePreview} alt="Preview" className="img-thumbnail" style={{ maxWidth: '200px' }} />
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={() => {
                                                setImagePreview(null);
                                                formik.setFieldValue('profile_image', null); // Clear image
                                            }}
                                        >
                                            Remove Image
                                        </button>
                                        <button
                                            type='button'
                                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                            style={{ marginLeft: '10px', marginBottom: '8px' }}
                                            onClick={handleDownload}
                                        >
                                            <span><i className="fa-solid fa-download"></i></span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Address */}
                            <div className="col-md-6">
                                <label htmlFor="address" className="form-label">
                                    Address
                                </label>
                                <textarea
                                    id="address"
                                    name="address"
                                    className="form-control"
                                    value={formik.values.address}
                                    onChange={formik.handleChange}
                                    placeholder="Enter address"
                                />
                            </div>

                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ClientForm;
